import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, map, Observable, of, startWith, takeUntil, tap } from 'rxjs';
import { BlockNavigationIfChange } from '../../../../core/models/block-navigation-if-change';
import { DropdownOption } from '../../../../core/models/dropdown-option';
import { FieldDeselector } from '../../../../core/models/field-deselector';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { FormControlService } from '../../../../core/services/form-control/form-control.service';
import { DropdownService } from '../../../../core/services/resources/dropdown.service';
import { ResourcesService } from '../../../../core/services/resources/resources.service';
import { ApplicationPageBaseComponent } from '../../../../shared/components/fcc-601/application-page-base/application-page-base.component';
import { FormNumberIndicatorComponent } from '../../../../shared/components/fcc-601/form-number-indicator/form-number-indicator.component';
import { EllipsisPipe } from '../../../../shared/pipes/ellipsis.pipe';
import { ApplicationPurposeCodeEnum } from '../../shared/constants/application-purpose-codes';
import { StepValidationResult } from '../../shared/models/step-validation-result';
import { AttachmentRequiredButtonComponent } from '../shared/components/attachment/attachment-required-button/attachment-required-button.component';
import { ApplicationPageFacadeService } from '../shared/services/application-page-facade/application-page-facade.service';
import { DirectEntryService } from '../shared/services/direct-entry/direct-entry.service';
import { GeneralInfoHelperMessages } from './constants/general-info-messages';
import { GeneralInfoRequest } from './models/general-info-request';
import { GeneralInfoResponse } from './models/general-info-response';
import { SelectTypeaheadDirective } from '../../../../shared/directives/select-typeahead/select-typeahead.directive';

@Component({
    selector: 'app-general-info',
    templateUrl: './general-info.component.html',
    styleUrl: './general-info.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatSelectModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatTooltipModule,
        MatIconModule,
        FormNumberIndicatorComponent,
        EllipsisPipe,
        NgClass,
        AttachmentRequiredButtonComponent,
        SelectTypeaheadDirective,
    ],
})
export class GeneralInfoComponent
    extends ApplicationPageBaseComponent<
        GeneralInfoRequest,
        GeneralInfoResponse,
        GeneralInfoResponse
    >
    implements OnInit, OnDestroy, BlockNavigationIfChange
{
    private readonly formDeselectService = inject(FormControlService);
    private readonly dropdownService = inject(DropdownService);
    private readonly directEntryService = inject(DirectEntryService);
    private readonly resourcesService = inject(ResourcesService);

    readonly messages: typeof GeneralInfoHelperMessages =
        GeneralInfoHelperMessages;

    /**
     * Code for STA that maps to the License Types dropdown.
     */
    readonly staCode: string = 'S';

    private readonly dependentFormControlsForResetOptions: FieldDeselector<GeneralInfoResponse> =
        {
            developmentalOrSta: {
                dependentFields: ['emergencySta'],
            },
        };

    private switchableApplicationPurposeCodes: ApplicationPurposeCodeEnum[] =
        [];

    radioServiceCodes: DropdownOption[] = [];
    licenseTypes: DropdownOption[] = [];
    applicationPurposes: DropdownOption[] = [];
    yesNoOptions: DropdownOption[] = [];
    useOfServices: DropdownOption[] = [];

    override form: FormGroup<FormGroupType<GeneralInfoResponse>> =
        new FormGroup<FormGroupType<GeneralInfoResponse>>({
            radioServiceCode: new FormControl('', Validators.required),
            existingRadioServiceCode: new FormControl({
                value: '',
                disabled: true,
            }),
            applicationPurpose: new FormControl(),
            developmentalOrSta: new FormControl('', Validators.required),
            emergencySta: new FormControl(undefined),
            callSign: new FormControl({ value: '', disabled: true }),
            requestedExpirationDate: new FormControl(
                { value: '', disabled: true },
                [],
            ),
            useOfServiceType: new FormControl({ value: '', disabled: true }),
            isApplicationMajor: new FormControl(undefined),
            hasAttachments: new FormControl(undefined),
        });

    constructor() {
        const route = inject(ActivatedRoute);
        const router = inject(Router);
        const applicationPageFacadeService = inject(
            ApplicationPageFacadeService,
        );

        super(route, router, applicationPageFacadeService);
    }

    override ngOnInit(): void {
        super.ngOnInit();
    }

    showEmergencySituation() {
        return this.form.value.developmentalOrSta === this.staCode;
    }

    protected override postFormBindingActions(): void {
        this.setupPostFormBindingFormDeselectors();

        this.loadDropdownData().subscribe(() => {
            this.setupFormState();
        });

        this.setupFormListeners();
    }

    protected override bindForm(response: GeneralInfoResponse): void {
        this.form.patchValue(response);
    }

    protected override getFormDataFromApi(
        id: string,
    ): Observable<GeneralInfoResponse> {
        return this.directEntryService.getGeneralInfo(id);
    }

    protected override saveFormDataToApi(
        request: GeneralInfoRequest,
    ): Observable<StepValidationResult> {
        return this.directEntryService.saveGeneralInfo(request);
    }

    protected override generateRequestObject(): GeneralInfoRequest {
        const formValue = this.form.value as Required<GeneralInfoResponse>;

        return {
            ...formValue,
            applicationId: this.applicationId!,
        };
    }

    /**
     * If the application purpose code has been changed, then it is necessary to update all of the steps
     * because not all steps are applicable to all application purpose codes. It is necessary to also update
     * all of the validations for the steps as well as there may be different validation requirements depending
     * on the application purpose code.
     */
    protected override postSaveActions(): Observable<void> {
        if (!this.applicationState) {
            throw new Error('Application state is not loaded.');
        }

        const originalApplicationPurposeCode: ApplicationPurposeCodeEnum | null =
            this.applicationState.applicationPurpose;
        if (!originalApplicationPurposeCode) {
            throw new Error(
                'Application state does not contain an application purpose code.',
            );
        }

        // If the application purpose has not changed, then no further actions are necessary.
        if (
            this.form.value.applicationPurpose ===
            originalApplicationPurposeCode
        ) {
            return of(void 0);
        }

        // Application purpose has changed, so update the steps and their validations in the direct entry flow.
        return this.directEntryService.getAndSetDirectEntryStepsWithValidations(
            this.applicationId!,
        );
    }

    /**
     * Sets up the form state after dropdown data has been loaded.
     *
     * Disables the application purpose control if the current application purpose
     * is not one of the switchable application purpose codes.
     */
    private setupFormState() {
        const { applicationPurpose } = this.form.value;
        this.guardAgainstMissingApplicationPurpose(applicationPurpose);

        // Disable application purpose if not one of the switchable purpose codes
        if (
            !this.switchableApplicationPurposeCodes.some(
                (code) => code === applicationPurpose,
            )
        ) {
            this.applicationPageFacadeService.applicationFormControlManager.disable(
                this.form.controls.applicationPurpose,
            );
        }
    }

    private setupFormListeners() {
        this.form.controls.developmentalOrSta?.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap((value) => this.updateEmergencyStaValidators(value)),
            )
            .subscribe();

        this.form.controls.applicationPurpose?.valueChanges
            .pipe(
                // Emit the current value immediately upon subscription so that the listener reacts to the initial state.
                startWith(this.form.controls.applicationPurpose.value),
                takeUntil(this.destroy$),
                tap((applicationPurpose) =>
                    this.updateIsApplicationMajorState(applicationPurpose),
                ),
            )
            .subscribe();
    }

    private updateEmergencyStaValidators(
        developmentalOrSta: string | null,
    ): void {
        const emergencyStaControl = this.form.controls.emergencySta;

        if (developmentalOrSta === this.staCode) {
            emergencyStaControl.setValidators([Validators.required]);
        } else {
            emergencyStaControl.clearValidators();
        }

        emergencyStaControl.updateValueAndValidity();
    }

    private loadDropdownData(): Observable<void> {
        return forkJoin({
            licenseTypes: this.dropdownService.getDemonstrationLicenseOrSTA(),
            radioServiceCodes: this.dropdownService.getRadioServiceCodes(),
            applicationPurposes: this.dropdownService.getApplicationPurposes(),
            yesNoOptions: this.dropdownService.getYesNoOptions(),
            useOfServices: this.dropdownService.getUseOfServices(),
            switchableApplicationPurposeCodes:
                this.resourcesService.getSwitchableApplicationPurposeCodes(),
        }).pipe(
            takeUntil(this.destroy$),
            tap((result) => {
                this.radioServiceCodes = result.radioServiceCodes;
                this.licenseTypes = result.licenseTypes;
                this.applicationPurposes =
                    this.filterApplicationPurposeDropdownOptions(
                        result.applicationPurposes,
                        result.switchableApplicationPurposeCodes,
                    );
                this.yesNoOptions = result.yesNoOptions;
                this.useOfServices = result.useOfServices;
                this.switchableApplicationPurposeCodes =
                    result.switchableApplicationPurposeCodes;
            }),
            map(() => void 0),
        );
    }

    private setupPostFormBindingFormDeselectors(): void {
        this.formDeselectService.setupFormDeselectorsHandler(
            this.form as FormGroup<FormGroupType<GeneralInfoResponse>>,
            this.dependentFormControlsForResetOptions,
            this.destroy$,
        );
    }

    /**
     * Updates the state of the "is application major" form control based on the application purpose.
     * If the application purpose is "renewal only", the control is disabled and set to true.
     * Otherwise, the control is enabled.
     *
     * @param applicationPurpose The current application purpose code from the form.
     */
    private updateIsApplicationMajorState(
        applicationPurpose: ApplicationPurposeCodeEnum | null,
    ): void {
        this.guardAgainstMissingApplicationPurpose(applicationPurpose);

        if (applicationPurpose === ApplicationPurposeCodeEnum.renewalOnly) {
            this.applicationPageFacadeService.applicationFormControlManager.disable(
                this.form.controls.isApplicationMajor,
                { emitEvent: false },
            );
            this.form.controls.isApplicationMajor.patchValue(true, {
                emitEvent: false,
            });
        } else {
            this.applicationPageFacadeService.applicationFormControlManager.enable(
                this.form.controls.isApplicationMajor,
                { emitEvent: false },
            );
        }
    }

    /**
     * Filters the application purpose dropdown options depending on if the form's current application purpose
     * is one of the switchable application purpose codes.
     *
     * If it is switchable, only the switchable options are shown.
     * If it is not switchable, only the current application purpose option is shown.
     *
     * @param applicationPurposeCodeDropdownOptions The full list of application purpose dropdown options.
     * @param switchableApplicationPurposeCodes The list of switchable application purpose codes.
     * @returns The filtered list of application purpose dropdown options.
     * @throws Error if the application purpose is not set in the form value as it is expected to be set.
     */
    private filterApplicationPurposeDropdownOptions(
        applicationPurposeCodeDropdownOptions: DropdownOption[],
        switchableApplicationPurposeCodes: ApplicationPurposeCodeEnum[],
    ): DropdownOption[] {
        const { applicationPurpose } = this.form.value;
        this.guardAgainstMissingApplicationPurpose(applicationPurpose);

        if (
            switchableApplicationPurposeCodes.includes(
                applicationPurpose as ApplicationPurposeCodeEnum,
            )
        ) {
            return applicationPurposeCodeDropdownOptions.filter((option) =>
                switchableApplicationPurposeCodes.includes(
                    option.value as ApplicationPurposeCodeEnum,
                ),
            );
        }

        return applicationPurposeCodeDropdownOptions.filter(
            (option) => option.value === applicationPurpose,
        );
    }

    /**
     * Guards against the application purpose being missing in the form value as it is expected to be set.
     *
     * @throws Error if the application purpose is not set in the form value.
     */
    private guardAgainstMissingApplicationPurpose(
        applicationPurpose: ApplicationPurposeCodeEnum | null | undefined,
    ): void {
        if (!applicationPurpose) {
            throw new Error(
                'Application purpose is not set in the form value.',
            );
        }
    }
}
